// fence.ts — Statements' FINANCIAL-ADVICE fence, enforced PRE-LLM (deterministic, zero deps).
//
// Extracted so a battery (fence-battery.mjs) can exercise it WITHOUT importing Tauri. The markers
// are kept byte-identical to `src-tauri/src/ipc.rs::ADVICE_MARKERS` and to the copy previously
// inlined in web-commands.ts — this file is now the single source, and both callers import it.
//
// WHY THIS FENCE IS HARD (finance vertical): the app reads a person's real bank statements. The
// line it must not cross is between DESCRIBING what the numbers say ("you spent £412 on groceries
// in June") and ADVISING what to do about it ("you should cut back"). The second is regulated
// financial advice. The fence runs PRE-LLM so a refusal is free and out of reach of a prompt
// injection aimed at the server prompt.
//
// ⚠️ Deliberately NOT over-broad. Bare "invest in" / "crypto" / "pay off my" were REMOVED (see the
// note in ipc.rs) because they blocked legitimate descriptive questions — "how much did I invest in
// my pension" is a question about the user's OWN ledger and must be answered. The advice sense of
// those words is already covered by "should i invest" / "good|bad investment" / "should i pay off".
// The battery's PASS column exists to keep that honest.

export const ADVICE_MARKERS: string[] = [
  'should i', 'should we', 'shall i', 'what should', 'ought i', 'is it worth',
  'good investment', 'bad investment', 'should i invest',
  'pay less tax', 'reduce my tax', 'avoid tax', 'tax efficient', 'tax advice',
  'how do i save', 'how can i save', 'help me budget', 'budget for me', 'make a budget',
  'set a budget', 'what budget', 'can i afford', 'should i buy', 'should i sell',
  'recommend', 'what do you recommend', 'advise me', 'give me advice', 'financial advice',
  'what stock', 'which stock', 'should i pay off', 'best way to',
  'will i have enough', 'predict', 'forecast my', 'should i switch',
  // advice-PHRASING markers — judgement-seeking asks that a noun-only list misses.
  'overspend', 'cut back', 'spend less', 'too much on', 'too high',
  'spending problem', 'what can i do',
];

export const ADVICE_REFUSAL =
  'Oriora Statements describes what your numbers say — it can\'t give financial, budgeting, ' +
  'investment, or tax advice. Try asking about what you actually spent, e.g. "How much did I ' +
  'spend on groceries last month?"';

/** Returns the refusal message if the question seeks advice, else null. The caller blocks the
 *  billed AI call when this returns a string. */
export function adviceRefusal(question: string): string | null {
  const q = String(question ?? '').toLowerCase();
  return ADVICE_MARKERS.some((m) => q.includes(m)) ? ADVICE_REFUSAL : null;
}
